import { useState } from 'react';
import { ExternalLink, RotateCw, Code2, Info } from 'lucide-react';

const DEV_URL = 'https://dev.skillsnap.me';

/**
 * Website Dev — sandbox for iterating on the landing page without touching prod.
 *
 * Source of truth is the LOCAL repo file `page_proposals/index_dev.html`;
 * it is deployed to the S3 bucket `skillsnap-dev-website` (served via the
 * dev.skillsnap.me CloudFront distribution) by scripts/upload_dev_website.py.
 * Edits are made via Work Queue requests with Target Area = `webpage-dev`
 * (scope-guarded in local/work_queue_process.py: edit local file → commit/push
 * → deploy to S3 — never direct-to-S3, to preserve git traceability).
 */
export function WebsiteDev() {
  const [reloadKey, setReloadKey] = useState(0);
  const [currentFile, setCurrentFile] = useState('index_dev.html');

  const reload = () => setReloadKey((k) => k + 1);

  const previewUrl = `${DEV_URL}/${currentFile === 'index.html' ? '' : currentFile}?dev=1&t=${reloadKey}`;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Code2 className="w-6 h-6 text-primary-600" />
            Website Dev
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Iteration sandbox for the landing page. Changes never reach the production webpage.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">File:</label>
          <select
            value={currentFile}
            onChange={(e) => { setCurrentFile(e.target.value); setReloadKey((k) => k + 1); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="index.html">index.html (current)</option>
            <option value="index_dev.html">index_dev.html</option>
          </select>
          <button
            onClick={reload}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 inline-flex items-center gap-1.5"
            title="Reload the preview iframe"
          >
            <RotateCw className="w-3.5 h-3.5" />
            Reload
          </button>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center gap-1.5"
          >
            Open in new tab
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4 flex items-start gap-2">
        <Info className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-primary-900">
          Each <code className="bg-white px-1 rounded text-xs">&lt;section&gt;</code> has an{' '}
          <span className="font-semibold">✎ Edit section</span> button in the top-right.
          Clicking one opens the Work Queue with the request prefilled — record your change and submit.
          Source: <code className="bg-white px-1 rounded text-xs">page_proposals/index_dev.html</code> (edited locally,
          committed to git, then deployed to S3)
        </div>
      </div>

      {/* Preview iframe */}
      <div className="rounded-xl border border-gray-300 overflow-hidden bg-white shadow-sm">
        <div className="bg-gray-800 text-gray-300 text-xs px-3 py-1.5 flex items-center justify-between font-mono">
          <span>{previewUrl}</span>
          <span className="text-green-400">● dev preview</span>
        </div>
        <iframe
          key={reloadKey}
          src={previewUrl}
          title="Dev landing page preview"
          className="w-full bg-white"
          style={{ height: 'calc(100vh - 240px)', minHeight: '600px', border: 0 }}
        />
      </div>
    </div>
  );
}
